import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '@config/firebase';
import { tenantsService, Tenant } from '@services/tenantsService';
import { purgeAllChatData } from '../components/dataQueryChat/storage';

interface TenantContextType {
  currentUser: User | null;
  adminSpaces: Tenant[];
  currentTenant: Tenant | null;
  currentTenantId: string | null;
  loading: boolean;
  switchTenant: (tenantId: string) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: React.ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [adminSpaces, setAdminSpaces] = useState<Tenant[]>([]);
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Get current tenant object from ID
  const currentTenant = adminSpaces.find(tenant => tenant.id === currentTenantId) || null;

  // Set up auth state listener and tenant subscription
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      
      if (user?.email) {
        // Subscribe to tenants for this admin
        tenantsService.subscribeToAdminTenants(user.email, (tenants) => {
          setAdminSpaces(tenants);
          
          // Auto-select first tenant if none selected and tenants available
          if (tenants.length > 0) {
            const savedTenantId = localStorage.getItem('selectedTenantId');
            const validSavedTenant = tenants.find(t => t.id === savedTenantId);
            
            if (validSavedTenant) {
              setCurrentTenantId(savedTenantId);
            } else {
              // Select first tenant if no valid saved selection
              setCurrentTenantId(tenants[0].id);
              localStorage.setItem('selectedTenantId', tenants[0].id);
            }
          } else {
            setCurrentTenantId(null);
            localStorage.removeItem('selectedTenantId');
          }
          
          setLoading(false);
        });
      } else {
        setAdminSpaces([]);
        setCurrentTenantId(null);
        localStorage.removeItem('selectedTenantId');
        purgeAllChatData();
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      tenantsService.unsubscribe();
    };
  }, []);

  const switchTenant = (tenantId: string) => {
    const tenant = adminSpaces.find(t => t.id === tenantId);
    if (tenant) {
      setCurrentTenantId(tenantId);
      localStorage.setItem('selectedTenantId', tenantId);
      console.log(`Switched to tenant: ${tenant.title} (${tenantId})`);
    }
  };

  const value: TenantContextType = {
    currentUser,
    adminSpaces,
    currentTenant,
    currentTenantId,
    loading,
    switchTenant,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};